import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TemplateProperties from "./TemplateProperties";
import { Box, Tabs, Tab, TabScrollButton, Badge, CircularProgress } from "@mui/material";
import TabPanel from "../TabPanel";
import TuneIcon from "@mui/icons-material/Tune";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useSelector } from "react-redux";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { Description } from "./Description";
import TextRotationNoneOutlinedIcon from "@mui/icons-material/TextRotationNoneOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import FindReplaceOutlinedIcon from "@mui/icons-material/FindReplaceOutlined";
import GlobalSubstitutions from "./GlobalSubstitutions";
import FieldLevelSubstitution from "./FieldLevelSubstitution";
import DefaultFieldTypeValues from "./DefaultFieldTypeValues";
import { LuReplace, LuReplaceAll } from "react-icons/lu";
import { MdOutlinePattern } from "react-icons/md";
import styled from "@emotion/styled";
import WorkflowProperties from "../WorkflowProperties/WorkflowProperties";
import ErrorHandlingProperties from "../WorkflowProperties/ErrorHandlingProperties";

const StyledTabs = styled(Tabs)(({ theme }) => ({
	"&.Mui-disabled": {
		opacity: 0.3,
	},
}));


function TemplatePropertiesContainer() {
	const { t } = useTranslation();
	const [currTab, setCurrTab] = useState(0);
	const [disabled, setDisabled] = useState(true);
	const parentScreen = useContext(ParentScreenCtx);
	const leftSelectedNode = useSelector(
		(state) => state.treeData[parentScreen].leftTree.tree.body.selectedNode
	);
	 const { rightTree } = useSelector(state=>state.treeData[parentScreen])
	 const templateHeader = rightTree.tree.templateHeader;
   const rightSelectedNode = rightTree.tree.body.selectedNode;
	// description component WIP
	let descriptionContent = "description content";

	const handleTabChange = (event, newValue) => {
		setCurrTab(newValue);
	};

	useEffect(() => {
		setDisabled(
			!["inboundTemplate", "outboundTemplate", "workflow"]?.includes(leftSelectedNode?.type)
		);
		setCurrTab(0);
	}, [leftSelectedNode?.key]);

  useEffect(() => {
    if(rightSelectedNode?.ICON_DISPLAY === "ERROR_HANDLER") setCurrTab(1);
  }, [rightSelectedNode?.key]);

	return (
    <div className="height-100 width-100" style={{position: "relative"}}>
      <Box sx={{ borderBottom: 1, borderColor: "divider", zIndex: "1000" }}>
        {parentScreen === "inbound" && (
          <StyledTabs
            scrollButtons={"auto"}
            variant="scrollable"
            sx={{ backgroundColor: "#fff" }}
            value={currTab}
            onChange={handleTabChange}>
            <Tab
              disabled={disabled}
              label={
                <div className="flex align-center">
                  <TuneIcon size="small" className="margin-right-4" />
                  {t("LBL_TEM_PROP")}
                </div>
              }
            />
            <Tab
              disabled={disabled}
              label={
                <div className="flex align-center">
                  <DescriptionOutlinedIcon
                    size="small"
                    className="margin-right-4"
                  />
                  <Badge
                    color="primary"
                    variant="dot"
                    invisible={!templateHeader?.NOTES?.trim()}>
                    <span>{t("DESCRIPTION")}</span>
                  </Badge>
                </div>
              }
            />
          </StyledTabs>
        )}

        {parentScreen === "outbound" && (
          <StyledTabs
            scrollButtons={"auto"}
            variant="scrollable"
            sx={{ backgroundColor: "#fff" }}
            value={currTab}
            onChange={handleTabChange}>
            <Tab
              disabled={disabled}
              label={
                <div className="flex align-center">
                  <TuneIcon size="small" className="margin-right-4" />
                  {t("LBL_TEM_PROP")}
                </div>
              }
            />
            <Tab
              disabled={disabled}
              label={
                <div className="flex align-center">
                  <DescriptionOutlinedIcon
                    size="small"
                    className="margin-right-4"
                  />
				  <Badge
                    color="primary"
                    variant="dot"
                    invisible={!templateHeader?.NOTES?.trim()}>
                    <span>{t("DESCRIPTION")}</span>
                  </Badge>
                </div>
              }
            />
            <Tab
              disabled={disabled}
              label={
                <div className="flex align-center">
                  <LuReplaceAll size={"18px"} className="margin-right-4" />
                  {t("LBL_GLOBAL_SUBSTITUTIONS")}
                </div>
              }
            />
            <Tab
              disabled={disabled}
              label={
                <div className="flex align-center">
                  <LuReplace size={"18px"} className="margin-right-4" />
                  {t("LBL_FLD_LEVEL_SUBSTITUTIONS")}
                </div>
              }
            />
            <Tab
              disabled={disabled}
              label={
                <div className="flex align-center">
                  <MdOutlinePattern size={"20px"} className="margin-right-4" />
                  {t("LBL_DEF_FLD_TYPE_VALUES")}
                </div>
              }
            />
          </StyledTabs>
        )}

        {parentScreen === "workflow" && (
          <StyledTabs
            scrollButtons={"auto"}
            variant="scrollable"
            sx={{ backgroundColor: "#fff" }}
            value={currTab}
            onChange={handleTabChange}>
            <Tab
              disabled={disabled}
              label={
                <div className="flex align-center">
                  <LuReplaceAll size={"18px"} className="margin-right-4" />
                  {t("LBL_WORKFLOW_PROPERTIES")}
                </div>
              }
            />
            <Tab
              disabled={disabled}
              label={
                <div className="flex align-center">
                  <DescriptionOutlinedIcon
                    size="small"
                    className="margin-right-4"
                  />
                  {t("LBL_ERROR_HANDLING_PROPS")}
                </div>
              }
            />
            <Tab
              disabled={disabled}
              label={
                <div className="flex align-center">
                  <DescriptionOutlinedIcon
                    size="small"
                    className="margin-right-4"
                  />
                  <Badge
                    color="primary"
                    variant="dot"
                    invisible={!templateHeader?.WORKFLOW?.COMMENTS?.trim()}>
                    <span>{t("DESCRIPTION")}</span>
                  </Badge>
                </div>
              }
            />
          </StyledTabs>
        )}
      </Box>
      {parentScreen === "inbound" && (
        <Box className="height-100-tab width-100">
          <TabPanel
            doNotRenderOnHidden={true}
            className="height-100 width-100"
            value={currTab}
            index={0}
            style={{ overflowY: "auto" }}>
            <TemplateProperties />
          </TabPanel>
          <TabPanel
            className="height-100 width-100"
            value={currTab}
            index={1}
            style={{ overflowY: "auto" }}>
            <Description tab={currTab} />
          </TabPanel>
        </Box>
      )}
      {parentScreen === "outbound" && (
        <Box className="height-100-tab width-100">
          <TabPanel
            className="height-100 width-100"
            value={currTab}
            index={0}
            style={{ overflowY: "auto" }}>
            <TemplateProperties />
          </TabPanel>
          <TabPanel
            className="height-100 width-100"
            value={currTab}
            index={1}
            style={{ overflowY: "auto" }}>
            <Description tab={currTab} />
          </TabPanel>
          <TabPanel
            className="height-100 width-100"
            value={currTab}
            index={2}
            style={{ overflowY: "auto" }}>
            <GlobalSubstitutions />
          </TabPanel>
          <TabPanel
            className="height-100 width-100"
            value={currTab}
            index={3}
            style={{ overflowY: "auto" }}>
            <FieldLevelSubstitution />
          </TabPanel>
          <TabPanel
            className="height-100 width-100"
            value={currTab}
            index={4}
            style={{ overflowY: "auto" }}>
            <DefaultFieldTypeValues />
          </TabPanel>
        </Box>
      )}
      {parentScreen === "workflow" && (
        !rightTree?.loading ? (
        <Box className="height-100-tab width-100">
          <TabPanel
            className="height-100 width-100"
            value={currTab}
            index={0}
            style={{ overflowY: "auto" }}>
            <WorkflowProperties />
          </TabPanel>
          {currTab === 1 && (
            <TabPanel
              className="height-100 width-100"
              value={currTab}
              index={1}
              style={{ overflowY: "auto" }}>
              <ErrorHandlingProperties />
            </TabPanel>
          )}
          <TabPanel className="height-100 width-100" value={currTab} index={2}>
            <Description tab={currTab} screen="workflow" />
          </TabPanel>
        </Box> )
        : <CircularProgress sx={{position: "absolute", top: "50%", left: "45%"}} />
      )}
    </div>
  );
}

export default TemplatePropertiesContainer;
